import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const WebContentWriting = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WEB CONTENT WRITING</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Professional Web Content That <span className="text-amber-700">Converts Visitors</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional web content writing for author websites, landing pages, and sales pages. SEO-optimized copy that engages visitors and drives book sales.
              </p>
              <Button onClick={() => setIsModalOpen(true)} className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                Get Content Quote
              </Button>
              <div className="grid grid-cols-4 gap-6 text-center mt-12">
                <div><div className="text-4xl font-bold text-amber-700 mb-2">200+</div><div className="text-gray-600 text-sm">Pages Written</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">50+</div><div className="text-gray-600 text-sm">Copywriters</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">All</div><div className="text-gray-600 text-sm">Art Styles</div></div>
                <div><div className="text-4xl font-bold text-amber-700 mb-2">100%</div><div className="text-gray-600 text-sm">Rights Included</div></div>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&h=600&fit=crop" alt="Illustration" className="rounded-2xl shadow-2xl w-full h-[500px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?w=1920&h=800&fit=crop" alt="Illustrations" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Illustrate Your Book?</h2>
            <p className="text-xl mb-12 opacity-90">Get a free quote and see our portfolio. Let's create stunning illustrations for your book.</p>
            <Button onClick={() => setIsModalOpen(true)} className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl transform hover:scale-105 transition-all">
              Get Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">What Our <span className="text-amber-700">Authors Say</span></h2></div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { name: 'Sarah Peterson', title: 'Children\'s Book Author', text: 'The illustrations are absolutely magical! They perfectly captured what I envisioned. Kids and parents love them!', rating: 5 },
              { name: 'Mike Johnson', title: 'Graphic Novel Author', text: 'Outstanding artwork and professionalism. The illustrator understood my vision immediately and delivered beyond expectations.', rating: 5 },
              { name: 'Emma Davis', title: 'Fantasy Author', text: 'Beautiful character designs and scene illustrations. The art elevated my story to a whole new level.', rating: 5 }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => (<span key={i} className="text-amber-500 text-xl">★</span>))}</div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div><div className="font-bold text-gray-900">{testimonial.name}</div><div className="text-sm text-gray-600">{testimonial.title}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16"><h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked <span className="text-amber-700">Questions</span></h2></div>
          <div className="max-w-3xl mx-auto">
            {[
              { question: 'How much does book illustration cost?', answer: 'Pricing depends on style and quantity. Children\'s book illustrations: $200-800 per page for professional quality. Character design: $500-1,500 per character. Full book packages available with volume discounts.' },
              { question: 'How long does illustration take?', answer: 'Timeline depends on complexity. Typical 32-page children\'s book: 6-10 weeks. Individual illustrations: 1-2 weeks per piece. Rush service available for urgent projects.' },
              { question: 'Do I own the illustration rights?', answer: 'Yes! You receive full commercial rights to all illustrations. You can use them in your book, marketing materials, merchandise, and any other commercial purposes.' },
              { question: 'Can I see the illustrator\'s portfolio first?', answer: 'Absolutely! We\'ll match you with illustrators whose style fits your book genre and vision. You\'ll review portfolios and select your preferred artist before any work begins.' }
            ].map((faq, index) => (
              <div key={index} className="mb-4">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all">
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? <ChevronUp className="w-6 h-6 text-amber-700" /> : <ChevronDown className="w-6 h-6 text-amber-700" />}
                </button>
                {openFaq === index && (<div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>)}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default WebContentWriting;
